import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import DeleteModal from "../../../../components/modals/DeleteModal";
import Icon from "../../../../components/common/Icon";
import ScannableObjectTagsModal from "./ScannableObjectTagsModal";
import {
    getScanConfig,
    getScannableObjects,
    deleteScannableObject,
    updateScannableObjectPositions,
    updateScannableObjectStatus,
    ScannableObjectResponse,
} from "../../../../api/progression";
import { useSortableSensors } from "../../../../hooks/useSortableSensors";
import i18n from "../../../../i18n";
import styles from "./ManageObjects.module.css";
import { ReactComponent as SettingsIcon } from "../../../../assets/images/settings-4-line.svg";
import stepStyles from "../createStep.module.css";

interface ManageObjectsProps {
    navigateToNext: () => void;
}

interface ObjectCardContentProps {
    obj: ScannableObjectResponse;
    localizedName: string;
    tagNames: string[] | null;
    onEdit: () => void;
    onDeleteClick: () => void;
    onToggleStatus: () => void;
    t: any;
}

/**
 * Visual content of an object card. Shared between the sortable list items
 * and the DragOverlay preview so both look identical (same dimensions).
 */
const ObjectCardContent: React.FC<ObjectCardContentProps> = ({
    obj,
    localizedName,
    tagNames,
    onEdit,
    onDeleteClick,
    onToggleStatus,
    t,
}) => (
    <>
        <div className={styles.objectCardHeader}>
            <div className={styles.objectImage}>
                {obj.referenceImageUrl ? (
                    <img src={obj.referenceImageUrl} alt={localizedName} />
                ) : (
                    <Icon name="ic-image" className={styles.objectImagePlaceholder} size={22} />
                )}
            </div>
            <div className={styles.objectInfo}>
                <div className={styles.objectNameRow}>
                    <h4 className={styles.objectName}>{localizedName}</h4>
                    <ToggleSwitch checked={obj.status} onChange={onToggleStatus} />
                </div>
                <div className={styles.objectMeta}>
                    <span className={styles.metaItem}>
                        {t("position")}: {obj.position}
                    </span>
                    {tagNames &&
                        tagNames.map((tagName, idx) => (
                            <span key={idx} className={`${styles.metaItem} ${styles.metaTag}`}>
                                {t("tag")}: {tagName}
                            </span>
                        ))}
                </div>
            </div>
        </div>

        <div className={styles.objectCardActions}>
            <div className={styles.actionButtons}>
                <Tooltip title={t("edit")} arrow>
                    <button
                        type="button"
                        className={styles.editBtn}
                        aria-label={t("edit")}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit();
                        }}
                    >
                        <Icon name="ic-edit" size={20} />
                    </button>
                </Tooltip>
                <Tooltip title={t("delete")} arrow>
                    <button
                        type="button"
                        className={styles.deleteBtn}
                        aria-label={t("delete")}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteClick();
                        }}
                    >
                        <Icon name="ic-trash" size={20} />
                    </button>
                </Tooltip>
            </div>
        </div>
    </>
);

interface SortableObjectCardProps extends ObjectCardContentProps {}

const SortableObjectCard: React.FC<SortableObjectCardProps> = (props) => {
    const { obj, onEdit } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: obj.id,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        // Hide the original while a DragOverlay preview follows the cursor so
        // the user only sees one copy of the card while dragging.
        opacity: isDragging ? 0 : 1,
        cursor: "grab",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${styles.objectCard} ${!obj.status ? styles.objectCardInactive : ""}`}
            onClick={onEdit}
            {...attributes}
            {...listeners}
        >
            <ObjectCardContent {...props} />
        </div>
    );
};

const ManageObjects: React.FC<ManageObjectsProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [objects, setObjects] = useState<ScannableObjectResponse[]>([]);
    const [scanConfigId, setScanConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [objectToDelete, setObjectToDelete] = useState<ScannableObjectResponse | null>(null);
    const [tagsModalOpen, setTagsModalOpen] = useState(false);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    const sensors = useSortableSensors();

    const loadObjects = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);
            const configResponse = await getScanConfig({ token, trackId: trackData.trackId });
            if (configResponse?.scanConfig) {
                setScanConfigId(configResponse.scanConfig.id);
                const objectsResponse = await getScannableObjects({
                    token,
                    scanConfigId: configResponse.scanConfig.id,
                });
                if (objectsResponse?.scannableObjects) {
                    setObjects(objectsResponse.scannableObjects.sort((a, b) => a.position - b.position));
                }
            }
        } catch (error) {
            console.error("Error loading scannable objects:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadObjects();
    }, [trackData.trackId, token]);

    const handleToggleStatus = async (objectId: string, currentStatus: boolean) => {
        // Optimistic update
        setObjects((prev) => prev.map((obj) => (obj.id === objectId ? { ...obj, status: !currentStatus } : obj)));
        try {
            await updateScannableObjectStatus({
                token,
                scannableObjectId: objectId,
                status: !currentStatus,
            });
        } catch (error) {
            console.error("Error updating object status:", error);
            // Revert on error
            setObjects((prev) => prev.map((obj) => (obj.id === objectId ? { ...obj, status: currentStatus } : obj)));
        }
    };

    const handleDelete = async () => {
        if (!objectToDelete) return;

        // Optimistic update
        const deletedObject = objectToDelete;
        setObjects((prev) => prev.filter((obj) => obj.id !== deletedObject.id));
        setDeleteModalOpen(false);
        setObjectToDelete(null);

        try {
            await deleteScannableObject({ token, scannableObjectId: deletedObject.id });
        } catch (error) {
            console.error("Error deleting object:", error);
            // Revert on error
            setObjects((prev) => [...prev, deletedObject].sort((a, b) => a.position - b.position));
        }
    };

    const linkId = trackData?.trackId || trackData?.id;

    const handleEdit = (objectId: string) => {
        navigate(`/create/tour/${linkId}/objects/modify/${objectId}`);
    };

    const handleCreate = () => {
        navigate(`/create/tour/${linkId}/objects/create`);
    };

    const handleManageTags = () => {
        setTagsModalOpen(true);
    };

    const getLocalizedName = (obj: ScannableObjectResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = obj.scannableObjectLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || obj.scannableObjectLocalized?.[0]?.name || t("unnamed");
    };

    const getTagNames = (obj: ScannableObjectResponse) => {
        if (!obj.tags || obj.tags.length === 0) return null;
        const langCode = i18n.language?.toUpperCase();
        return obj.tags.map((tag) => {
            const localized = tag.scannableObjectTagLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
            return localized?.name || tag.scannableObjectTagLocalized?.[0]?.name || t("unnamed");
        });
    };

    const sortedObjects = useMemo(() => {
        return [...objects].sort((a, b) => {
            const posA = a.position || 0;
            const posB = b.position || 0;
            if (posA !== posB) return posA - posB;
            return getLocalizedName(a).localeCompare(getLocalizedName(b));
        });
    }, [objects]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveDragId(String(event.active.id));
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveDragId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = sortedObjects.findIndex((o) => o.id === active.id);
        const newIndex = sortedObjects.findIndex((o) => o.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(sortedObjects, oldIndex, newIndex);
        const updated = reordered.map((obj, index) => ({ ...obj, position: index + 1 }));

        // Identify which objects actually changed position before mutating state
        const changed = updated.filter((obj) => {
            const prev = sortedObjects.find((o) => o.id === obj.id);
            return prev && prev.position !== obj.position;
        });

        // Optimistic update
        setObjects(updated);

        try {
            await updateScannableObjectPositions({
                token,
                positions: changed.map((obj) => ({
                    scannableObjectId: obj.id,
                    position: obj.position,
                })),
            });
        } catch (error) {
            console.error("Error reordering objects:", error);
            await loadObjects();
        }
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/main-theme`}>{t("content")}</Link>
                <span>›</span>
                {t("objects")}
            </div>

            {/* Header — experience-objets-b .obj-head + .obj-sub */}
            <div className={styles.pageHead}>
                <div className={styles.pageHeadText}>
                    <TitleSubtitle title={t("createAndManageObjects")} subtitle="" layout="objects" />
                </div>
                <div className={styles.headerActions}>
                    <Tooltip title={t("manageTags")} arrow>
                        <button
                            onClick={handleManageTags}
                            className={styles.tagsButton}
                            type="button"
                            disabled={!scanConfigId}
                        >
                            <SettingsIcon className={styles.tagsButtonIcon} />
                        </button>
                    </Tooltip>
                    <AddButton onClick={handleCreate} tooltip={t("addNewObject")} disabled={!scanConfigId} />
                </div>
            </div>
            <p className={styles.pageSub}>{t("defineObjectsUsersWillScanDescription")}</p>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : objects.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noObjectsYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noObjectsDescription")}</p>
                    <AddButton onClick={handleCreate} tooltip={t("createFirstObject")} disabled={!scanConfigId} />
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setActiveDragId(null)}
                >
                    <SortableContext items={sortedObjects.map((o) => o.id)} strategy={rectSortingStrategy}>
                        <div className={styles.objectsGrid}>
                            {sortedObjects.map((obj) => (
                                <SortableObjectCard
                                    key={obj.id}
                                    obj={obj}
                                    localizedName={getLocalizedName(obj)}
                                    tagNames={getTagNames(obj)}
                                    onEdit={() => handleEdit(obj.id)}
                                    onDeleteClick={() => {
                                        setObjectToDelete(obj);
                                        setDeleteModalOpen(true);
                                    }}
                                    onToggleStatus={() => handleToggleStatus(obj.id, obj.status)}
                                    t={t}
                                />
                            ))}
                        </div>
                    </SortableContext>
                    <DragOverlay dropAnimation={null}>
                        {activeDragId
                            ? (() => {
                                  const obj = sortedObjects.find((o) => o.id === activeDragId);
                                  if (!obj) return null;
                                  return (
                                      <div
                                          className={`${styles.objectCard} ${!obj.status ? styles.objectCardInactive : ""}`}
                                          style={{ cursor: "grabbing" }}
                                      >
                                          <ObjectCardContent
                                              obj={obj}
                                              localizedName={getLocalizedName(obj)}
                                              tagNames={getTagNames(obj)}
                                              onEdit={() => {}}
                                              onDeleteClick={() => {}}
                                              onToggleStatus={() => {}}
                                              t={t}
                                          />
                                      </div>
                                  );
                              })()
                            : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Continue — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    disabled={objects.length === 0}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {objectToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setObjectToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteObjectConfirmation")}
                    title={t("deleteObject")}
                />
            )}

            {/* Tags management modal */}
            {scanConfigId && (
                <ScannableObjectTagsModal
                    isOpen={tagsModalOpen}
                    onClose={() => setTagsModalOpen(false)}
                    scanConfigId={scanConfigId}
                    language={trackData.language || "EN"}
                />
            )}
        </div>
    );
};

export default ManageObjects;
